/**
 * Menu utilisateur — identité, rôle, organisation, préférences, déconnexion.
 *
 * POURQUOI REGROUPER
 * ------------------
 * La barre du haut portait treize éléments, dont cinq pour la seule identité : nom, badge de
 * rôle, identifiant d'organisation, préférences d'affichage, bouton de déconnexion. Ils
 * occupaient une place permanente alors qu'on les consulte rarement et qu'on n'agit dessus
 * presque jamais — au détriment de ce qu'on utilise à chaque minute.
 *
 * L'avatar reste visible en permanence : savoir SOUS QUELLE IDENTITÉ on travaille compte dans
 * un produit où la séparation des devoirs décide qui peut approuver quoi. Le badge « lecture
 * seule » reste également visible hors du menu, car il explique pourquoi des actions sont
 * indisponibles — le cacher ferait passer une restriction de droits pour une panne.
 */
import type { JSX } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BellRing, Building2, ListFilter, MailOpen, Rows3, ShieldCheck } from 'lucide-react'

import { canAccessAdministration, canWrite, estLectureSeule, logout, roleSession, type Session } from '@/auth/session'
import type { NavigationMode } from '@/lib/navigationMode'
import { compteNavigation } from '@/lib/nav'
import { APP_VERSION, BUILD_DATE } from '@/lib/version'
import { api } from '@/api/client'
import { ADRESSE_REGLAGES_NOTIFICATIONS } from '@/api/preferencesNotificationApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DisplayMenuContent } from '@/components/DisplayMenu'
import { useNomOrganisation } from '@/hooks/useProfilOrganisation'
import { useT } from '@/i18n/language'

export function UserMenu({
  session, navigationMode, onNavigationModeChange, navigationModeError,
  navigationModeConflict, onConfirmNavigationModeFreshValue,
}: {
  session: Session
  navigationMode: NavigationMode
  onNavigationModeChange: (mode: NavigationMode) => void
  navigationModeError: string | undefined
  /** v030-po-auth-03 : une autre session a changé la préférence entre-temps — la valeur
   *  fraîche du serveur, à confirmer explicitement avant qu'elle ne remplace l'affichage. */
  navigationModeConflict?: { mode: NavigationMode } | null | undefined
  onConfirmNavigationModeFreshValue?: (() => void) | undefined
}): JSX.Element {
  const { t } = useT()
  const naviguer = useNavigate()
  const nomOrganisation = useNomOrganisation(session.organisationId)
  const writable = canWrite(session)
  const lectureSeule = estLectureSeule(session)
  // v05x-org-neuve : la clé suit le rôle (plus de table libellé → clé qui oubliait org_admin et auditor).
  const role = roleSession(session)
  const roleTxt = t(role.cle, role.libelle)
  const initiales = (session.username || '?').slice(0, 2).toUpperCase()
  const hiddenCount = compteNavigation(session.roles, navigationMode).masquees

  // B-007 (ART-0166) : le pied de la barre latérale (`AppSidebar`, `data-testid="app-version"`)
  // est hors du DOM visible sur mobile (tiroir fermé par défaut) et illisible une fois le
  // panneau replié en mode icône — le menu utilisateur, lui, reste monté et cliquable dans
  // les DEUX cas. La version/date de la PASSERELLE n'était nulle part dans l'interface avant
  // ce correctif (`/actuator/info` était public mais vide — carte-passerelle.md §10) ;
  // `staleTime: Infinity` : ces valeurs sont figées pour la durée de vie de l'onglet, un
  // nouveau déploiement recharge de toute façon la page.
  const gatewayVersion = useQuery({
    queryKey: ['gateway-version'],
    queryFn: api.gatewayVersion,
    staleTime: Infinity,
  })

  return (
    <div className="flex shrink-0 items-center gap-2">
      {/* Hors du menu, à dessein : une restriction de droits doit être visible sans clic,
          sinon elle passe pour une panne quand un bouton reste inactif. */}
      {lectureSeule ? (
        <Badge variant="outline" data-write={false}
               className="hidden sm:inline-flex"
               style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning-text)' }}>
          {t('app.readonlyShort', 'Lecture seule')}
        </Badge>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid="user-menu"
                  aria-label={t('user.menu', 'Compte et préférences')}>
            <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold"
                  aria-hidden="true">
              {initiales}
            </span>
            {/* Le nom ET LE RÔLE restent dans le DOM en permanence : ils nomment le bouton
                pour les lecteurs d'écran, et restent vérifiables sans ouvrir le menu.
                Le rôle avait été replié dans le menu, où il n'était plus lisible qu'après un
                clic — or dans un produit où la séparation des devoirs décide qui peut
                approuver quoi, savoir sous quelle habilitation on agit ne se demande pas :
                cela se voit. Le badge « Lecture seule » reste visible à l'écran par-dessus,
                puisqu'il explique pourquoi des actions sont indisponibles. */}
            <span className="sr-only" data-testid="current-user">{session.username}</span>
            <span className="sr-only" data-testid="current-role" data-write={writable}>
              {roleTxt}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="truncate font-medium">{session.username}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {roleTxt}
              {session.organisationId ? (
                <> · <span data-testid="current-org" title={session.organisationId}>{nomOrganisation ?? session.organisationId}</span></>
              ) : null}
            </span>
          </DropdownMenuLabel>

          {/* Lot ORG 0.5.x : la fiche de l'organisation courante (lecture pour tout membre,
              identité et intégrations modifiables par son administration). */}
          {session.organisationId ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-mon-organisation" onSelect={() => naviguer('/org/fiche')}>
                <Building2 className="mr-2 size-4" aria-hidden="true" />
                {t('menu.monOrganisation', 'Mon organisation')}
              </DropdownMenuItem>
            </>
          ) : null}

          {/* Accès à l'ADMINISTRATION DE LA PLATEFORME (2026-09-30 : « il n'y a aucune vue
              d'administration ») — la barre haute v0.5.0 n'offrait aucun chemin vers le niveau
              Plateforme ; seule l'URL y menait. Réservé au rôle `admin`, comme le niveau lui-même. */}
          {session.roles.includes('admin') ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-administration-plateforme"
                                onSelect={() => naviguer('/plateforme/sante')}>
                <ShieldCheck className="mr-2 size-4" aria-hidden="true" />
                {t('menu.administrationPlateforme', 'Administration de la plateforme')}
              </DropdownMenuItem>
            </>
          ) : null}

          {/* Lot NAV 0.5.x (registre n° 12) : l'accueil de l'administration (demandes d'accès,
              invitations, suspensions, santé, raccourcis), pour qui peut administrer. */}
          {canAccessAdministration(session) ? (
            <DropdownMenuItem data-testid="menu-accueil-administration" onSelect={() => naviguer('/administration')}>
              <ShieldCheck className="mr-2 size-4" aria-hidden="true" />
              {t('menu.accueilAdministration', 'Accueil administration')}
            </DropdownMenuItem>
          ) : null}
          {/* Lot NAV 0.5.x (A2 constat 8) : les invitations reçues, pour tous. */}
          <DropdownMenuItem data-testid="menu-invitations" onSelect={() => naviguer('/invitations')}>
            <MailOpen className="mr-2 size-4" aria-hidden="true" />
            {t('menu.invitations', 'Mes invitations')}
          </DropdownMenuItem>

          {/* SRF-TRV-049 : préférence PERSONNELLE — ouverte à tous les rôles, lecteur compris. */}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="menu-reglages-notifications"
                            onSelect={() => naviguer(ADRESSE_REGLAGES_NOTIFICATIONS)}>
            <BellRing className="mr-2 size-4" aria-hidden="true" />
            {t('menu.reglagesNotifications', 'Réglages de notification')}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DisplayMenuContent />

          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="navigation-mode-toggle"
            onSelect={() => onNavigationModeChange(navigationMode === 'discovery' ? 'complete' : 'discovery')}
          >
            {navigationMode === 'discovery'
              ? <ListFilter className="mr-2 size-4" aria-hidden="true" />
              : <Rows3 className="mr-2 size-4" aria-hidden="true" />}
            {/* Audit 2026-10-09 (P4) : ces libellés étaient écrits en dur, en français jusque dans
                l'interface anglaise. */}
            <span className="flex flex-1 flex-col">
              <span>
                {navigationMode === 'discovery'
                  ? t('user.navModeAfficherTout', 'Afficher toutes les entrées')
                  : t('user.navModeEssentiel', 'Revenir à l’essentiel')}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('user.navModeMasquees', '{{n}} entrées masquées', { n: hiddenCount })}
              </span>
            </span>
          </DropdownMenuItem>
          {navigationModeError ? <p role="alert" className="px-2 py-1 text-xs text-destructive">{navigationModeError}</p> : null}
          {navigationModeConflict ? (
            <div role="alert" data-testid="navigation-mode-conflict"
                 className="flex flex-col gap-1 px-2 py-1.5 text-xs">
              <span className="text-muted-foreground">
                {navigationModeConflict.mode === 'discovery'
                  ? t('user.navModeConflitEssentiel', 'Un autre appareil a choisi « essentiel » entre-temps.')
                  : t('user.navModeConflitToutAfficher', 'Un autre appareil a choisi « tout afficher » entre-temps.')}
              </span>
              <Button type="button" size="sm" variant="outline" className="h-7 self-start"
                      data-testid="navigation-mode-conflict-confirm"
                      onClick={onConfirmNavigationModeFreshValue}>
                {t('user.navModeConflitUtiliser', 'Utiliser cette valeur')}
              </Button>
            </div>
          ) : null}

          <DropdownMenuSeparator />
          {/* B-007 (ART-0166) : visible ici — jamais seulement dans le pied de la barre
              latérale — pour rester lisible sur mobile (tiroir fermé) et panneau replié.
              Deux lignes distinctes : l'interface (déjà connue, `@/lib/version`) et la
              passerelle (nouvelle, `GET /api/v1/public/version`), jamais confondues. */}
          <div className="flex flex-col gap-0.5 px-2 py-1.5 text-xs text-muted-foreground">
            <span data-testid="app-version-menu" className="tabular-nums">
              {t('app.versionInterfaceLine', 'Interface {{version}} · {{date}}', {
                version: APP_VERSION,
                date: BUILD_DATE || t('app.versionDateUnknown', 'date inconnue'),
              })}
            </span>
            <span data-testid="gateway-version-menu" className="tabular-nums">
              {gatewayVersion.data
                ? t('app.versionGatewayLine', 'Passerelle {{version}} · {{date}}', {
                    version: gatewayVersion.data.version,
                    date: gatewayVersion.data.built_at || t('app.versionDateUnknown', 'date inconnue'),
                  })
                : t('app.versionGatewayUnknown', 'Passerelle : indisponible')}
            </span>
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="logout" onSelect={logout}>
            {t('app.logout', 'Se déconnecter')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
